import { Col, DatePicker, Row, Select, Space, Table, Tooltip, Empty } from 'antd';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
	syncLogsAction,
	syncLogsPaginateAction,
} from 'redux/action/syncLogsAction';
import { AppDispatch } from 'redux/store';
import styles from './index.module.scss';
import './index.scss';
import moment from 'moment';

const { RangePicker } = DatePicker;

const SyncLogsTable = () => {
	const { Column } = Table;
	const tableRef = useRef<HTMLDivElement>(null);

	const dispatch = useDispatch<AppDispatch>();

	const { data, count } = useSelector((state: any) => state?.syncLogs);

	const [syncLogsData, setSyncLogsData] = useState([]);
	const [isInViewPort, setIsInViewPort] = useState<boolean>(false);
	const [pageNo, setPageNo] = useState(1);
	const [filterValue, setFilterValue] = useState<any>('');
	const [date, setDate] = useState<any>({
		fromDate: null,
		toDate: null,
	});

	const { selectedCompanyDetails } = useSelector(
		(state: any) => state.companies
	);


	const getSyncLogsData = async (isPageable?: boolean) => {
		if (isPageable) {
			setPageNo((prev: any) => prev + 1);
		} else {
			setIsInViewPort(false);
			setPageNo(1);
		}

		const query: any = {
			page: isPageable ? pageNo + 1 : 1,
			limit: 20,
			filter: filterValue,
			fromDate: date?.fromDate,
			toDate: date?.toDate,
		};

		if (!filterValue || filterValue == '') {
			delete query['filter'];
		}

		if (!date.fromDate || !date.toDate) {
			delete query['fromDate'];
			delete query['toDate'];
		}

		if (isPageable) {
			await dispatch(syncLogsPaginateAction(query));
			return;
		}
		await dispatch(syncLogsAction(query));
	};

	useEffect(() => {
		getSyncLogsData();
	}, [filterValue, date, selectedCompanyDetails]);

	useEffect(() => {
		(async () => {
			if (isInViewPort && data.length < count) {
				await getSyncLogsData(true);
			}
		})();
	}, [isInViewPort]);

	const scrollHandler = useCallback((event: any) => {
		const { scrollTop, scrollHeight, clientHeight } = event.target;
		// If scrolled to bottom (with a small threshold)
		if (scrollTop + clientHeight >= scrollHeight - 10) {
			setIsInViewPort(true);
		} else {
			setIsInViewPort(false);
		}
	}, []);

	const dateHandler = (value: any) => {
		if (value) {
			setDate({
				fromDate: value[0]['$d'],
				toDate: value[1]['$d'],
			});
		} else {
			setDate({
				fromDate: null,
				toDate: null,
			});
		}
	};

	useEffect(() => {
		if (tableRef.current) {
			const tableBody = tableRef.current.querySelector('.ant-table-body');

			if (tableBody) {
				tableBody.addEventListener('scroll', scrollHandler);
			}

			return () => {
				// Remove scroll event listener when component unmounts
				if (tableBody) {
					tableBody.removeEventListener('scroll', scrollHandler);
				}
			};
		}
	}, [data, scrollHandler]);

	useEffect(() => {
		const syncData = data.map((singleLog: any) => {
			return {
				key: singleLog.id,
				date: singleLog.date,
				time: singleLog.time,
				module: singleLog.module,
				status: singleLog.status == 1 ? 'Success' : 'Failed',
				message: singleLog.message,
			};
		});
		setSyncLogsData(syncData);
	}, [data]);

	const hasActiveFilters = Boolean(
		filterValue ||
		(date?.fromDate && date?.toDate)
	);

	const CustomEmptyState = () => (
		<Empty
			image={Empty.PRESENTED_IMAGE_SIMPLE}
			description={
				hasActiveFilters
					? "No Sync Log records match the selected filter criteria"
					: "No Sync Log records are currently available"
			}
			style={{
				padding: '40px 20px',
				margin: '20px 0'
			}}
		/>
	);

	return (
		<div className={'dynamic-table dynamic-table-new'} ref={tableRef}>
			<div className={`${styles['search-filter']}`}>
				<div className={styles['search-filter__wrapper']}>
					<Row gutter={18}>
						<Col>
							<Space>
								<Select
									defaultValue="All"
									style={{ width: 200 }}
									onChange={(key: any, value: any) => {
										setFilterValue(value?.value);
									}}
									options={[
										{ label: 'All Modules', value: '' },
										{ label: 'Time Activity', value: 'Time Activity' },
										{ label: 'Employee', value: 'Employee' },
										{ label: 'Journal', value: 'Journal' },
										{ label: 'Expense Journal', value: 'Expense Journal' },
										{ label: 'Transaction Sync', value: 'Transaction Sync' },
										{ label: 'Transaction Update', value: 'Transaction Update' },
										{ label: 'Attachment Zip', value: 'Attachment Zip' },
									]}
									size="large"
								/>
							</Space>
						</Col>
						<Col>
							<Space direction="vertical" size={8}>
								<RangePicker
									size="large"
									onChange={dateHandler}
									format={'MM/DD/YYYY'}
								/>
							</Space>
						</Col>
					</Row>
				</div>
			</div>
			<Table
				dataSource={syncLogsData}
				pagination={false}
				scroll={{ y: '59vh', x: 'max-content' }}
				tableLayout="fixed" // ✅ Add this
				locale={{
					emptyText: <CustomEmptyState />
				}}
			>
				<Column
					// title="Date"
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Date
						</p>
					)}
					dataIndex="date"
					key="date"
					render={(value) => {
						return (
							<span className={styles['dynamic-table__date']}>
								{moment(value).format('MM/DD/YYYY')}
							</span>
						);
					}}
					className="bg-white"
					width={120}
				// width="15%"
				/>
				<Column
					// title="Time"
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Time
						</p>
					)}
					dataIndex="time"
					key="time"
					// render={(value) => moment(value).format('hh:mm A')}
					render={(value) => {
						return (
							<span className={styles['dynamic-table__time']}>
								{moment(value).format('hh:mm A')}
							</span>
						);
					}}
					className="bg-white"
					width={120}
				// width="15%"
				/>
				<Column
					// title="Module"
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Module
						</p>
					)}
					dataIndex="module"
					key="module"
					// render={(value) => value}
					render={(value) => {
						return (
							<span className={styles['dynamic-table__module']}>{value}</span>
						);
					}}
					className="bg-white"
					width={150}
				// width="15%"
				/>
				<Column
					// title="Status"
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Status
						</p>
					)}
					dataIndex="status"
					key="status"
					render={(value) => {
						return (
							<div>
								<p className={styles[value]}>{value}</p>
							</div>
						);
					}}
					className="bg-white"
					width={150}
				// width="15%"
				/>
				<Column
					// title="Message"
					title={() => (
						<p
							style={{
								fontSize: '12px',
							}}
						>
							Message
						</p>
					)}
					dataIndex="message"
					key="message"
					// render={(value) => value}
					render={(value) => {
						return (
							<Tooltip
								title={
									<div
										style={{
											maxHeight: '150px', // Set max height for scrollable content
											overflowY: 'auto', // Enable vertical scrolling
										}}
									>
										{value}
									</div>
								}
								overlayStyle={{ maxWidth: '400px' }} // Set max width for the tooltip
							>
								<span className={styles['message-column']}>{value}</span>
							</Tooltip>
						);
					}}
					className={`bg-white ${styles['message-column']}`}
					width={400}
				// width="40%"
				/>
			</Table>
		</div>
	);
};

export default SyncLogsTable;
